import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Supervisor, SupervisoryLevel } from '../../../features/users/users.model';
import { UsersService } from '../../../features/users/users.service';

@Component({
  selector: 'app-supervisor-picker-modal',
  standalone: false,
  templateUrl: './supervisor-picker-modal.component.html',
  styleUrl: './supervisor-picker-modal.component.scss'
})
export class SupervisorPickerModalComponent implements OnChanges {
  private usersService = inject(UsersService);

  @Input() isOpen = false;
  @Input() supervisoryLevel: SupervisoryLevel | null = null;
  @Input() selectedSupervisorId: string | null = null;

  @Output() supervisorSelected = new EventEmitter<Supervisor>();
  @Output() closed = new EventEmitter<void>();

  supervisors: Supervisor[] = [];
  loading = false;
  error: string | null = null;
  searchQuery = '';

  /** The level we need to fetch (one step above the current user's level) */
  get targetLevel(): string {
    if (this.supervisoryLevel === 'L1') return 'L2';
    if (this.supervisoryLevel === 'L2') return 'L3';
    return '';
  }

  get targetLevelLabel(): string {
    if (this.supervisoryLevel === 'L1') return 'Supervisors (L2)';
    if (this.supervisoryLevel === 'L2') return 'Regional Managers (L3)';
    return '';
  }

  get filteredSupervisors(): Supervisor[] {
    if (!this.searchQuery.trim()) return this.supervisors;
    const q = this.searchQuery.toLowerCase();
    return this.supervisors.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.role.toLowerCase().includes(q)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen && this.supervisoryLevel) {
        this.searchQuery = '';
        this.fetchSupervisors();
      } else {
        this.searchQuery = '';
      }
    }
  }

  private fetchSupervisors(): void {
    this.loading = true;
    this.error = null;
    this.supervisors = [];

    this.usersService.findSupervisors(this.targetLevel).subscribe({
      next: (data) => {
        this.supervisors = data;
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load supervisors. Please try again.';
        this.loading = false;
      }
    });
  }

  retry(): void {
    this.fetchSupervisors();
  }

  select(supervisor: Supervisor): void {
    this.supervisorSelected.emit(supervisor);
  }

  close(): void {
    this.closed.emit();
  }
}
